import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingBag, FileText, BarChart2, Users, LogOut, Menu, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { to: '/admin', icon: <LayoutDashboard size={18} />, label: 'Dashboard', end: true },
  { to: '/admin/products', icon: <Package size={18} />, label: 'Produktet' },
  { to: '/admin/orders', icon: <ShoppingBag size={18} />, label: 'Porositë' },
  { to: '/admin/inventory', icon: <BarChart2 size={18} />, label: 'Inventari' },
  { to: '/admin/blog', icon: <FileText size={18} />, label: 'Blog' },
  { to: '/admin/users', icon: <Users size={18} />, label: 'Përdoruesit' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar${collapsed ? ' collapsed' : ''}`}>
        <div className="sidebar-header">
          {!collapsed && (
            <div className="sidebar-logo">
              <span style={{ color: 'var(--gold)' }}>HairFix</span>
              <span> Admin</span>
            </div>
          )}
          <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <ChevronRight size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              {item.icon}
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div className="sidebar-user">
              <div className="sidebar-avatar">{user?.name?.[0]}</div>
              <div>
                <div className="sidebar-user-name">{user?.name}</div>
                <div className="sidebar-user-role">Admin</div>
              </div>
            </div>
          )}
          <button className="sidebar-logout" onClick={handleLogout} title="Dil">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        <div className="admin-topbar">
          <NavLink to="/" className="back-to-site" target="_blank">
            ← Kthehuni te faqja
          </NavLink>
        </div>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>

      <style>{`
        .admin-layout { display:flex;min-height:100vh;background:#F1F5F3; }
        .admin-sidebar {
          width:240px;background:var(--green-deep);display:flex;flex-direction:column;
          position:sticky;top:0;height:100vh;transition:width .25s;flex-shrink:0;
        }
        .admin-sidebar.collapsed { width:64px; }
        .sidebar-header { display:flex;align-items:center;justify-content:space-between;padding:20px 16px;border-bottom:1px solid rgba(255,255,255,.1);min-height:64px; }
        .sidebar-logo { font-family:'Playfair Display',serif;font-size:1.1rem;font-weight:700;color:white;white-space:nowrap;overflow:hidden; }
        .sidebar-toggle { width:32px;height:32px;border-radius:8px;border:none;background:rgba(255,255,255,.1);color:white;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .2s; }
        .sidebar-toggle:hover { background:rgba(255,255,255,.2); }
        .sidebar-nav { flex:1;padding:12px 8px;display:flex;flex-direction:column;gap:2px;overflow-y:auto; }
        .sidebar-link { display:flex;align-items:center;gap:12px;padding:11px 10px;border-radius:var(--radius-md);color:rgba(255,255,255,.65);font-size:.875rem;font-weight:500;transition:all .2s;white-space:nowrap;overflow:hidden; }
        .sidebar-link:hover { background:rgba(255,255,255,.1);color:white; }
        .sidebar-link.active { background:rgba(255,255,255,.15);color:white;font-weight:600; }
        .sidebar-link svg { flex-shrink:0; }
        .sidebar-footer { padding:16px 8px;border-top:1px solid rgba(255,255,255,.1);display:flex;align-items:center;gap:10px; }
        .sidebar-user { display:flex;align-items:center;gap:10px;flex:1;overflow:hidden; }
        .sidebar-avatar { width:36px;height:36px;border-radius:50%;background:var(--gold);color:var(--green-deep);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:.9rem;flex-shrink:0; }
        .sidebar-user-name { font-size:.82rem;font-weight:600;color:white;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
        .sidebar-user-role { font-size:.7rem;color:rgba(255,255,255,.5);text-transform:uppercase;letter-spacing:.04em; }
        .sidebar-logout { width:36px;height:36px;border-radius:50%;border:none;background:rgba(255,255,255,.1);color:rgba(255,255,255,.7);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .2s; }
        .sidebar-logout:hover { background:rgba(220,38,38,.3);color:white; }
        .admin-main { flex:1;display:flex;flex-direction:column;overflow:hidden; }
        .admin-topbar { background:white;border-bottom:1px solid var(--cream-dark);padding:14px 28px;display:flex;align-items:center; }
        .back-to-site { font-size:.82rem;color:var(--text-light);font-weight:500;transition:color .2s; }
        .back-to-site:hover { color:var(--green-deep); }
        .admin-content { flex:1;padding:28px;overflow-y:auto; }
      `}</style>
    </div>
  );
}
